import {
  Clock,
  EyeOff,
  Inbox,
  Link2,
  ListChecks,
  Mail,
  MessagesSquare,
  SlidersHorizontal,
} from "lucide-react";
import { useEffect, useState } from "react";

import { Shell } from "@/components/Shell";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { Link } from "@/lib/router";
import { ladderLabel, type LadderId } from "@/shared/ladders";
import { type ScaleValue } from "@/shared/scale";
import type { Profile } from "@/shared/types";

interface Stats {
  groups: number;
  questions: number;
  settings: { matchThreshold: number; settleHours: number };
}

function Stat({ value, label }: { value: string; label: string }) {
  return (
    <div>
      <div className="text-2xl font-semibold tracking-tight text-gradient">{value}</div>
      <div className="mt-0.5 text-[13px] text-muted-foreground">{label}</div>
    </div>
  );
}

function Step({
  n,
  icon: Icon,
  title,
  children,
}: {
  n: number;
  icon: typeof Mail;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <div className="relative flex gap-4">
      <div className="flex flex-col items-center">
        <div className="grid size-10 shrink-0 place-items-center rounded-full border border-brand/25 bg-brand/10 text-brand">
          <Icon className="size-[18px]" />
        </div>
        <div className="mt-2 w-px flex-1 bg-gradient-to-b from-border to-transparent" />
      </div>
      <div className="pb-9">
        <div className="text-[11px] font-medium uppercase tracking-[0.09em] text-muted-foreground">
          Step {n}
        </div>
        <h3 className="mt-1 text-[17px] font-semibold tracking-tight">{title}</h3>
        <p className="mt-1.5 text-[15px] leading-relaxed text-muted-foreground">{children}</p>
      </div>
    </div>
  );
}

function Feature({
  icon: Icon,
  title,
  children,
}: {
  icon: typeof Mail;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <div className="rounded-2xl border border-border/70 bg-card/50 p-5 backdrop-blur-sm">
      <Icon className="size-[19px] text-brand" />
      <h3 className="mt-3 text-[15px] font-semibold tracking-tight">{title}</h3>
      <p className="mt-1.5 text-[14px] leading-relaxed text-muted-foreground">{children}</p>
    </div>
  );
}

/** A miniature, non-interactive slider, purely to explain the scale. */
function ScaleDemo() {
  const rows: { label: string; value: ScaleValue; ladder: LadderId }[] = [
    { label: "Swimming", value: 3, ladder: "taste" },
    { label: "Running", value: 0, ladder: "taste" },
    { label: "The gym", value: -2, ladder: "taste" },
    { label: "Being monogamous", value: 1, ladder: "agreement" },
  ];
  return (
    <div className="rounded-2xl border border-border/70 bg-card/50 p-5 backdrop-blur-sm">
      {rows.map((r) => {
        const pos = ((r.value + 3) / 6) * 100;
        return (
          <div key={r.label} className="py-2.5">
            <div className="mb-2 flex items-baseline justify-between gap-3 text-[13.5px]">
              <span className="min-w-0 truncate">{r.label}</span>
              <span
                className="shrink-0 font-medium"
                style={{
                  color:
                    r.value === 0
                      ? "var(--muted-foreground)"
                      : r.value < 0
                        ? "var(--negative)"
                        : "var(--positive)",
                }}
              >
                {ladderLabel(r.ladder, r.value)}
              </span>
            </div>
            <div className="relative h-2 rounded-full bg-muted">
              {r.value !== 0 && (
                <div
                  className="absolute top-0 h-2 rounded-full opacity-85"
                  style={{
                    left: `${Math.min(50, pos)}%`,
                    width: `${Math.abs(pos - 50)}%`,
                    background: r.value < 0 ? "var(--negative)" : "var(--positive)",
                  }}
                />
              )}
              <div className="absolute left-1/2 top-1/2 h-3 w-px -translate-x-1/2 -translate-y-1/2 bg-border" />
              <div
                className="absolute top-1/2 size-3.5 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-background shadow"
                style={{
                  left: `${pos}%`,
                  background:
                    r.value === 0
                      ? "var(--muted-foreground)"
                      : r.value < 0
                        ? "var(--negative)"
                        : "var(--positive)",
                }}
              />
            </div>
          </div>
        );
      })}
      <div className="mt-3 border-t border-border/60 pt-3 text-[12px] leading-relaxed text-muted-foreground">
        Each question names its own two ends — <em>hate / love</em>,{" "}
        <em>open / strict</em>, <em>now / later</em> — and the slider says how firmly you
        hold it: <em>leaning</em>, <em>somewhat</em>, <em>definitely</em>.
      </div>
    </div>
  );
}

export function Landing({ profile }: { profile: Profile | null }) {
  const [stats, setStats] = useState<Stats | null>(null);

  useEffect(() => {
    api.get<Stats>("/api/stats").then(setStats).catch(() => {});
  }, []);

  const threshold = Math.round((stats?.settings.matchThreshold ?? 0.75) * 100);
  const settleHours = stats?.settings.settleHours ?? 24;

  return (
    <Shell
      nav={
        <Button asChild size="sm" className="rounded-full">
          <Link href={profile ? "/profile" : "/join"}>
            {profile ? "Your profile" : "Get started"}
          </Link>
        </Button>
      }
    >
      {/* Hero */}
      <section className="py-16 sm:py-24">
        <div className="inline-flex items-center gap-2 rounded-full border border-brand/25 bg-brand/8 px-3 py-1 text-[12.5px] text-brand">
          <span className="relative flex size-1.5">
            <span className="absolute inline-flex size-full animate-ping rounded-full bg-brand opacity-60" />
            <span className="relative inline-flex size-1.5 rounded-full bg-brand" />
          </span>
          No swiping. No feed. No app to check.
        </div>

        <h1 className="mt-6 text-[2.6rem] font-semibold leading-[1.05] tracking-[-0.03em] sm:text-6xl">
          Dating that happens
          <br />
          <span className="text-gradient">while you're not looking.</span>
        </h1>

        <p className="mt-6 max-w-xl text-[17px] leading-relaxed text-muted-foreground">
          Drag some sliders to say what you love and what you can't stand. Close the tab. If
          someone joins who genuinely lines up — {threshold}% or better — we email you both.
          That's the entire product.
        </p>

        <div className="mt-8 flex flex-wrap items-center gap-3">
          <Button asChild size="lg" className="rounded-full px-7 text-[15px]">
            <Link href={profile ? "/profile" : "/join"}>
              {profile ? "Back to your profile" : "Create your profile"}
            </Link>
          </Button>
          <span className="text-[13.5px] text-muted-foreground">
            Ten minutes now, then nothing, possibly for a while.
          </span>
        </div>

        <div className="mt-14 grid grid-cols-2 gap-6 border-t border-border/60 pt-8 sm:grid-cols-4">
          <Stat value={`${stats?.questions ?? "—"}`} label="questions that count" />
          <Stat value={`${stats?.groups ?? "—"}`} label="areas of life" />
          <Stat value="0" label="photos, ever" />
          <Stat value="1" label="button to press" />
        </div>
      </section>

      {/* The scale */}
      <section className="py-8">
        <h2 className="text-[13px] font-medium uppercase tracking-[0.1em] text-muted-foreground">
          One question type, all the way down
        </h2>
        <div className="mt-6 grid gap-6 sm:grid-cols-2 sm:items-center">
          <div>
            <h3 className="text-[19px] font-semibold tracking-tight">
              Everything is a slider from −3 to +3.
            </h3>
            <p className="mt-3 text-[15px] leading-relaxed text-muted-foreground">
              Plus means you like it, minus means you don't, and the middle means you genuinely
              don't mind. Compatibility is then just how far apart the two of you put the same
              slider.
            </p>
            <p className="mt-3 text-[15px] leading-relaxed text-muted-foreground">
              A question neither of you cares about drops out entirely rather than counting as
              agreement — so answering everything with a shrug doesn't buy you a high score.
            </p>
          </div>
          <ScaleDemo />
        </div>
      </section>

      {/* How it works */}
      <section className="py-10">
        <h2 className="text-[13px] font-medium uppercase tracking-[0.1em] text-muted-foreground">
          How it works
        </h2>
        <div className="mt-8">
          <Step n={1} icon={Mail} title="Give us an email address">
            That's the whole signup. We send a link, you click it, and you stay signed in on that
            device. No password to forget.
          </Step>
          <Step n={2} icon={ListChecks} title="Say who you are, and who you'd like">
            Name, gender, birthday, where you are and what you speak. Then the three things that
            are absolute: the genders you want, the age range, and a shared language.
          </Step>
          <Step n={3} icon={SlidersHorizontal} title="Answer as much as you can bear">
            {stats?.questions ?? "A hundred or so"} sliders across{" "}
            {stats?.groups ?? "ten"} areas of life. Mark anything that really matters as
            important and it counts for far more.
          </Step>
          <Step n={4} icon={Clock} title={`Your answers settle for ${settleHours} hours`}>
            Edits only reach the matcher a full day after you stop fiddling. It stops the matcher
            being farmed, and it quietly encourages you to answer as you are rather than as you'd
            like to be read.
          </Step>
          <Step n={5} icon={Inbox} title="One day an email arrives">
            It shows their bio and how you scored in each area, and links to the full picture:
            every question you both answered, their answer beside yours. Decide on that, then
            press the one button. If they press it too, you swap contact details and we get out
            of the way.
          </Step>
        </div>
      </section>

      {/* Principles */}
      <section className="py-10">
        <h2 className="text-[13px] font-medium uppercase tracking-[0.1em] text-muted-foreground">
          What we deliberately don't do
        </h2>
        <div className="mt-6 grid gap-4 sm:grid-cols-2">
          <Feature icon={MessagesSquare} title="No chat, anywhere">
            We don't host messages and we don't relay them. When you match you get contact
            details — Signal, a phone number, a throwaway account, whatever you're comfortable
            with.
          </Feature>
          <Feature icon={EyeOff} title="Answers only when it matters">
            Nobody browsing ever sees what you put on a slider — a stranger with your link gets
            percentages at most. Your answers open in exactly two cases: to a match deciding
            whether to say yes, and to someone you handed your share password. Both only ever see
            questions they've answered themselves, beside their own answer.
          </Feature>
          <Feature icon={Link2} title="A public link, if you want one">
            Off by default. Turn it on and you get a short link showing your name, your country
            and a few lines you wrote. Anyone signed in who opens it sees how well you two score.
            Add a password and whoever you trust with it can open the answers behind those
            percentages — you choose whether that covers everything or leaves the private areas
            out.
          </Feature>
          <Feature icon={Clock} title="No engagement loop">
            Nothing to check, no streaks, no "someone viewed you". Being ignored by this app is
            the normal state. We'd rather write to you twice a year and be right.
          </Feature>
        </div>
      </section>

      {/* Honesty */}
      <section className="py-10">
        <div className="rounded-3xl border border-border/70 bg-card/50 p-7 backdrop-blur-sm sm:p-10">
          <h2 className="text-2xl font-semibold tracking-tight">
            The whole thing rests on you being honest.
          </h2>
          <p className="mt-4 max-w-2xl text-[15.5px] leading-relaxed text-muted-foreground">
            On every other dating app your profile is a pitch, so of course people round
            themselves up. Here nobody browsing ever reads your answers. They're shown only to a
            match weighing whether to say yes, or to someone you trusted with your password —
            people who have put their own answers on the table first. Rounding yourself up buys
            you nothing except a stranger who is wrong for you.
          </p>
          <p className="mt-4 max-w-2xl text-[15.5px] leading-relaxed text-muted-foreground">
            Say you can't stand hiking. Say you go to church. Say you want children, or that you
            certainly don't. The only person a flattering answer costs is you.
          </p>
          <div className="mt-7">
            <Button asChild size="lg" className="rounded-full px-7">
              <Link href={profile ? "/profile" : "/join"}>Start being honest</Link>
            </Button>
          </div>
        </div>
      </section>

      <section className="pb-6">
        <div className="rounded-3xl bg-gradient-to-br from-brand to-brand-alt px-7 py-12 text-center text-primary-foreground sm:px-10">
          <h2 className="text-3xl font-semibold tracking-tight">Then forget about it.</h2>
          <p className="mx-auto mt-3 max-w-md text-[15.5px] leading-relaxed opacity-90">
            Ten minutes now, and possibly nothing for months. That's not a flaw in the design.
          </p>
          <Button
            asChild
            size="lg"
            variant="secondary"
            className="mt-7 rounded-full px-8 text-[15px]"
          >
            <Link href={profile ? "/profile" : "/join"}>Create your profile</Link>
          </Button>
        </div>
      </section>
    </Shell>
  );
}
